import React, { MouseEvent } from 'react';
import { connect } from 'react-redux';
import { SIDEBAR_TYPE, SideNavGroup } from '@amzn/aac-chrome-internal-api';
import { Icon } from '@amzn/storm-ui';
import { externalLinkAlt } from '@amzn/storm-ui-icons';
import {
  Divider,
  GroupWrapper,
  ItemWrapper,
  SideNavLinkWrapper,
  SideNavWrapper,
  SubTitleWrapper,
  TitleWrapper,
} from './styles';
import {
  selectLandingAppName,
  updateLandingAppName,
  updateSelectedNavItem,
} from 'src/features/nav/store/slice';
import { RootState, store } from 'src/app/store';
import { NavMetricPublisher } from 'src/commons/utils/metrics';
import {
  LANDING_PLACEMENT,
  LandingAppIcon,
} from 'src/commons/components/LandingAppIcon/LandingAppIcon';
import { useIsRTL } from 'src/commons/hooks';
import { selectIsSpoofing } from 'src/features/header/store/slice';
import { useChromeConfigContext } from 'src/providers/ChromeConfigContextProvider';

interface SideNavProps {
  activeCategory: string;
  toolbarItemId: string;
  sidebarTitle: string;
  sidebarGroups: Array<SideNavGroup>;
  sidebarType?: SIDEBAR_TYPE;
  landingAppName: string;
  isSpoofing: boolean;
  updateActiveCategory: (...args: any[]) => any;
  updateLandingAppName: (...args: any[]) => any;
}

export const SideNav: React.FC<SideNavProps> = ({
  activeCategory,
  toolbarItemId,
  sidebarTitle,
  sidebarGroups,
  sidebarType,
  landingAppName,
  isSpoofing,
  updateActiveCategory,
  updateLandingAppName,
}) => {
  const isRtl = useIsRTL();
  const chromeConfig = useChromeConfigContext();
  const isOpen = activeCategory === toolbarItemId;
  const title = <TitleWrapper>{sidebarTitle}</TitleWrapper>;

  const content = sidebarGroups.map((group, groupIndex) => {
    const subtitle = group.title &&
      group.title.length > 0 &&
      group.title !== sidebarTitle && (
        <SubTitleWrapper key={group.title}>{group.title}</SubTitleWrapper>
      );
    const items = group.links.map((sideNavLink) => {
      const isExternalLink = sideNavLink.sidebarItemType === 'EXTERNAL_LINK';
      const isToolbar = sidebarType === SIDEBAR_TYPE.TOOL;
      // backwards compatibility with toolbar logic
      const showExternalLink = isToolbar || isExternalLink;

      const onNavLinkClick = async (event: MouseEvent) => {
        NavMetricPublisher.publishCounterMonitor(
          `sidenavItemClicked-${sideNavLink.id}`,
          1,
        );

        // check if csr is used
        const onRouteClick = chromeConfig.onRouteClick;
        if (sideNavLink.isCSREnabled && onRouteClick) {
          event.preventDefault();
          onRouteClick({
            url: sideNavLink.link.url,
            id: sideNavLink.id,
          });
          store.dispatch(
            updateSelectedNavItem({
              toolbarItemId,
              linkId: sideNavLink.id,
            }),
          );
          NavMetricPublisher.publishCounterMonitor(
            `sidenavItemClientSideRoutingClicked-${sideNavLink.id}`,
            1,
          );
          updateActiveCategory('');
        }
      };

      return (
        <ItemWrapper isSelected={sideNavLink.isSelected} key={sideNavLink.id}>
          <SideNavLinkWrapper
            href={sideNavLink.link.url}
            data-takt-id={sideNavLink.id}
            data-ccx-e2e-id={sideNavLink.id}
            tabIndex={activeCategory === toolbarItemId ? 0 : -1}
            target={showExternalLink ? '_blank' : '_self'}
            onClick={onNavLinkClick}
          >
            {sideNavLink.link.label}
            {showExternalLink && <Icon type={externalLinkAlt} size="xs" />}
          </SideNavLinkWrapper>
          {!isSpoofing &&
            !showExternalLink &&
            !sideNavLink.isInternal &&
            (landingAppName === '' ||
              landingAppName === sideNavLink.appName) && (
              <LandingAppIcon
                appName={sideNavLink.appName ?? ''}
                tooltipLabel={sideNavLink.link.label}
                landingAppName={landingAppName}
                landingPlacement={LANDING_PLACEMENT.CONSOLIDATED_NAV}
                updateLandingAppName={updateLandingAppName}
                tabIndex={activeCategory === toolbarItemId ? 0 : -1}
              />
            )}
        </ItemWrapper>
      );
    });

    return (
      <React.Fragment key={group.title}>
        {groupIndex > 0 && <Divider role="separator" />}
        <GroupWrapper
          onKeyDown={(e) => {
            if (e.key === 'Escape') updateActiveCategory('');
          }}
        >
          {subtitle}
          {items}
        </GroupWrapper>
      </React.Fragment>
    );
  });

  return (
    <>
      <SideNavWrapper
        data-ccx-e2e-id={isOpen ? 'aac-sidenav-active' : undefined}
        isOpen={isOpen}
        isRtl={isRtl}
      >
        <GroupWrapper>{title}</GroupWrapper>
        {content}
      </SideNavWrapper>
    </>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    landingAppName: selectLandingAppName(state),
    isSpoofing: selectIsSpoofing(state),
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    updateLandingAppName: (category) =>
      dispatch(updateLandingAppName(category)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(SideNav);
